import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatRadioGroup, MatRadioModule } from '@angular/material/radio';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { FormConfig, FormField, FormValues } from '@blaze-shared/connectors-domain';
import { CheckboxComponent, PasswordVisibilityToggleDirective } from '@blaze-shared/shared-ui';

@UntilDestroy()
@Component({
  selector: 'blaze-form-builder',
  imports: [
    CheckboxComponent,
    FormsModule,
    MatRadioGroup,
    MatRadioModule,
    PasswordVisibilityToggleDirective,
    ReactiveFormsModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './form-builder.component.html',
  styleUrls: ['./form-builder.component.scss'],
})
export class FormBuilderComponent implements OnChanges {
  private readonly fb = inject(UntypedFormBuilder);

  @Input() formConfig: FormConfig | null = null;
  @Input() initialValues: FormValues | undefined;
  @Output() formData = new EventEmitter<FormValues>();
  @Output() formValid = new EventEmitter<boolean>();

  formGroup = new UntypedFormGroup({});

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes?.['formConfig']?.currentValue?.length) {
      return;
    }
    const formControls = this.createFormControls(this.formConfig || []);
    this.formGroup = this.fb.group(formControls);
    this.formGroup.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.formData.emit(value);
    });
    this.formGroup.statusChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.formValid.emit(this.formGroup.valid);
    });

    this.fillFormWithValues(this.initialValues);
  }

  fillFormWithValues(values?: FormValues): void {
    if (!values) {
      return;
    }
    this.formGroup.patchValue(values);
  }

  isVisible(field: FormField): boolean {
    const parentId = field?.parent?.id || '';
    const expectedParentValue = field?.parent?.value || true; // true for checkboxes
    const parentValue = this.formGroup?.value?.[parentId];

    if (!parentId) {
      return true;
    }
    return parentValue === expectedParentValue;
  }

  showError(fieldName: string): boolean {
    const field = this.formGroup.controls[fieldName];
    return field.touched && field.invalid;
  }

  private createFormControls(fields: Array<FormField>): { [key: string]: UntypedFormControl } {
    const controls: { [key: string]: UntypedFormControl } = {};
    for (const field of fields) {
      const fieldValue = field?.defaultValue || '';
      if (field.name) {
        controls[field.name] = this.fb.control(fieldValue, this.getValidatorsForField(field));
      } else if (field.id) {
        controls[field.id] = this.fb.control(fieldValue, this.getValidatorsForField(field));
      }
    }
    return controls;
  }

  private getValidatorsForField(field: FormField): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    if (field?.isRequired) {
      validators.push(Validators.required);
    }
    return validators;
  }
}
